import React from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { v4 as uuid } from 'uuid';
import useFirestore, { useFirestoreUserPreferences } from './useFirestore';
import { ClientNotification, NotificationType } from '../util/utilities/notifications';
import useCachedQuery from './useCachedQuery';
import useQuery from './useQuery';
import useNotificationsSettings, { Notification } from './useNotificationsSettings';

export const useDisabledNotifications = (): [boolean, string[]] => {
    const query = useQuery<Notification[]>('user/notification-settings');
    const notificationNames =
        query.data
            ?.filter(
                notification =>
                    notification.channels.find(channel => channel.type === useNotificationsSettings.CHANNELS.Web)?.enabled ===
                    false
            )
            .map(notification => NotificationType[notification.type]) ?? [];
    return [query.loading, notificationNames];
};

export const useNotificationTypes = (): [boolean, number[], Error | undefined, () => Promise<any>] => {
    const typesQuery = useCachedQuery<Notification[]>('user/notification-settings');
    const notificationTypes =
        typesQuery.data
            ?.filter(notification =>
                notification.channels.find(channel => channel.type === useNotificationsSettings.CHANNELS.Web)
            )
            .map(notification => notification.type) ?? [];

    return [typesQuery.loading, notificationTypes, typesQuery.error, typesQuery.refetch];
};

const mapNotification = (
    notification: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
): ClientNotification => {
    const notificationData = notification.data();

    return {
        ...(notificationData as ClientNotification),
        id: notification.id,
        // necessary purely because of the data (type 2) in staging db
        createdAt: notificationData.createdAt.toDate ? notificationData.createdAt.toDate() : new Date(notificationData.createdAt),
    };
};

const groupUnseenNotifications = (
    user: firebase.firestore.DocumentReference,
    notifications: ClientNotification[]
): ClientNotification[] => {
    const deduplicateTypes = (uniqueTypes: NotificationType[], type: NotificationType) =>
        uniqueTypes.includes(type) ? uniqueTypes : [...uniqueTypes, type];
    // Reuse an existing groupId, otherwise generate a new one.
    const getGroupId = (type: NotificationType) =>
        notifications.find(n => n.type === type && n.groupId !== undefined)?.groupId || uuid();
    // Builds a map of type and groupId to use.
    const buildGroupIds = (groupIds: any, type: NotificationType) => ({ ...groupIds, [type]: getGroupId(type) });

    const groupIds = notifications
        .filter(n => n.type !== NotificationType.Custom)
        .map(n => n.type)
        .reduce(deduplicateTypes, [])
        .filter(type => notifications.filter(n => n.type === type).length > 1)
        .reduce(buildGroupIds, {});

    // Skip update if there is nothing to group
    if (Object.keys(groupIds).length < 1) {
        return notifications;
    }

    const batch = user?.firestore.batch();

    const notificationsWithGroups = notifications.map(notification => {
        const groupId = groupIds[notification.type] || null;

        if (groupId === null) {
            return { ...notification };
        }

        if (notification.groupId === undefined) {
            // If a group has never been determined for this notification, save that
            const ref = user.collection('notifications').doc(notification.id);
            batch.update(ref, { groupId });
        }

        return {
            ...notification,
            groupId,
        };
    });

    batch.commit().catch(() => {});

    return notificationsWithGroups;
};

const fetchNotifications = async (
    user: firebase.firestore.DocumentReference | undefined,
    types: number[],
    limit = 0,
    startAfterDoc: firebase.firestore.DocumentData | undefined = undefined
): Promise<[ClientNotification[], firebase.firestore.DocumentData | undefined, firebase.firestore.DocumentData | undefined]> => {
    const data: ClientNotification[] = [];
    let results: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData> | undefined;

    try {
        if (user) {
            let collection = user.collection('notifications').orderBy('createdAt', 'desc').where('type', 'in', types);

            if (limit) {
                collection = collection.limit(limit);
            }

            if (startAfterDoc) {
                collection = collection.startAfter(startAfterDoc);
            }

            results = await collection.get();
            results?.forEach(notification => data.push(mapNotification(notification)));
        }
    } catch (error) {
        // oh well
    }

    return [data.filter(n => types.includes(n.type)), getFirst(results?.docs), getLast(results?.docs)];
};

const getLast = <T>(array: T[] | undefined): T | undefined => (array && array.length > 0 ? array[array.length - 1] : undefined);

const getFirst = <T>(array: T[] | undefined): T | undefined => (array && array.length > 0 ? array[0] : undefined);

export const useNotifications = (
    limit = 50
): [ClientNotification[], () => Promise<number> | undefined, (...notifications: ClientNotification[]) => void] => {
    const [, user] = useFirestore();
    const [notifications, setNotifications] = React.useState<ClientNotification[]>([]);
    const [lastDoc, setLastDoc] = React.useState<firebase.firestore.DocumentData | undefined>(undefined);
    const [firstDoc, setFirstDoc] = React.useState<firebase.firestore.DocumentData | undefined>(undefined);
    const [loadingTypes, supportedTypes] = useNotificationTypes();

    const fetchMore = async (): Promise<number> => {
        const [newNotifications, newFirstDoc, newLastDoc] = await fetchNotifications(user, supportedTypes, limit, lastDoc);
        setNotifications(currentNotifications => [...currentNotifications, ...newNotifications]);

        if (newLastDoc !== undefined) {
            setLastDoc(newLastDoc);
        }

        if (firstDoc === undefined) {
            setFirstDoc(newFirstDoc);
        }

        return newNotifications?.length;
    };

    React.useEffect(() => {
        if (supportedTypes.length > 0) {
            fetchMore();
        }
    }, [loadingTypes]);

    React.useEffect(() => {
        if (!user || !firstDoc || supportedTypes.length < 1) {
            return;
        }

        const unsubscribe = user
            .collection('notifications')
            .orderBy('createdAt', 'desc')
            .where('type', 'in', supportedTypes)
            .endBefore(firstDoc)
            .onSnapshot(snapshot => {
                const data: ClientNotification[] = [];
                snapshot.forEach(notification => {
                    data.push(mapNotification(notification));
                });
                setNotifications(currentNotifications => [
                    ...data,
                    ...currentNotifications.filter(n => !data.some(n2 => n2.id === n.id)),
                ]);
            });

        return unsubscribe;
    }, [firstDoc]);

    const markNotificationsAsRead = (...notificationsToUpdate: ClientNotification[]) => {
        if (!user || !notificationsToUpdate) {
            return;
        }

        const batch = user?.firestore.batch();
        const updatedNotificationsCache = [...notifications];

        notificationsToUpdate.forEach(notification => {
            const ref = user.collection('notifications').doc(notification.id);
            batch.update(ref, { read: true });

            updatedNotificationsCache.find(n => n.id === notification.id)!.read = true;
        });

        batch.commit().catch(() => {});
        setNotifications(updatedNotificationsCache);
    };

    return [notifications, fetchMore, markNotificationsAsRead];
};

export const useLatestNotifications = (): [
    ClientNotification[],
    Date | undefined,
    () => void,
    (...notifications: ClientNotification[]) => void,
] => {
    const [, user] = useFirestore();
    const [initialized, setInitialized] = React.useState(false);
    const [preferences, setPreferences, loadingPreferences] = useFirestoreUserPreferences();
    const lastSeenNotificationsTimestamp = preferences?.lastSeenNotificationsTimestamp;
    const [notifications, setNotifications] = React.useState<ClientNotification[]>([]);
    const [loadingTypes, supportedTypes] = useNotificationTypes();

    // Listen for changes to notifications
    React.useEffect(() => {
        if (!lastSeenNotificationsTimestamp || !initialized || supportedTypes.length < 1) {
            return;
        }

        const unsubscribe = user
            ?.collection('notifications')
            .orderBy('createdAt', 'desc')
            .where('type', 'in', supportedTypes)
            .where('createdAt', '>', lastSeenNotificationsTimestamp)
            .limit(100)
            .onSnapshot(snapshot => {
                if (snapshot.docChanges().length > 0) {
                    setNotifications(currentNotifications => {
                        const newNotifications = [...currentNotifications];

                        snapshot.docChanges().forEach(notification => {
                            const changedNotification = mapNotification(notification.doc);
                            const currentNotification = newNotifications.find(n => n.id === changedNotification.id);
                            if (currentNotification) {
                                Object.assign(currentNotification, changedNotification);
                            }
                        });

                        return newNotifications;
                    });
                }
            });
        return unsubscribe;
    }, [loadingPreferences, loadingTypes, initialized]);

    // Listen for new notifications
    React.useEffect(() => {
        if (!lastSeenNotificationsTimestamp || supportedTypes.length < 1) {
            return;
        }

        // This gives a notification 5 minutes from the time it was published to get to firestore.
        const timestampWithBuffer = new Date(
            lastSeenNotificationsTimestamp.setMinutes(lastSeenNotificationsTimestamp.getMinutes() - 5)
        );

        const unsubscribe = user
            ?.collection('notifications')
            .orderBy('createdAt', 'desc')
            .where('type', 'in', supportedTypes)
            .where('createdAt', '>', timestampWithBuffer)
            .limit(100)
            .onSnapshot(snapshot => {
                // Ignore local updates
                if (snapshot.metadata.hasPendingWrites) {
                    return;
                }

                const data: ClientNotification[] = [];
                snapshot.forEach(notification => {
                    data.push(mapNotification(notification));
                });

                // If there are no new notifications since the last seen time,
                // fetch the last 5 so the list isn't empty
                if (!initialized && snapshot.empty) {
                    fetchNotifications(user, supportedTypes, 5).then(([data]) => {
                        setInitialized(true);
                        setNotifications(data);
                    });
                } else {
                    // It is possible that realtime update data will contain already read notifications.
                    const unread = data.filter(n => !n.read);
                    const read = data.filter(n => n.read);
                    setNotifications(currentNotifications => [
                        ...groupUnseenNotifications(user, unread),
                        ...read,
                        // Exclude unseen notifications in the cache
                        ...currentNotifications.filter(n => !data.some(n2 => n2.id === n.id)),
                    ]);
                    setInitialized(true);
                }
            });

        return unsubscribe;
    }, [lastSeenNotificationsTimestamp, loadingPreferences, loadingTypes, initialized]);

    const setLastSeenTimestamp = () => setPreferences({ lastSeenNotificationsTimestamp: new Date() });

    const markNotificationsAsRead = (...notificationsToUpdate: ClientNotification[]) => {
        if (!user || !notificationsToUpdate) {
            return;
        }

        const batch = user?.firestore.batch();
        const updatedNotificationsCache = [...notifications];

        // Notifications in the same group will be updated.
        const notificationsInTheSameGroup = notifications.filter(
            n => n.groupId !== undefined && !n.read && notificationsToUpdate.some(ntu => ntu.groupId === n.groupId)
        );

        const allToUpdate = [...notificationsToUpdate.filter(n => !n.read), ...notificationsInTheSameGroup];

        allToUpdate.forEach(notification => {
            const ref = user.collection('notifications').doc(notification.id);
            batch.update(ref, { read: true });

            updatedNotificationsCache.find(n => n.id === notification.id)!.read = true;
        });

        batch.commit().catch(() => {});
        setNotifications(updatedNotificationsCache);
    };

    return [notifications, lastSeenNotificationsTimestamp, setLastSeenTimestamp, markNotificationsAsRead];
};
